import React, { lazy, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { fetchDatHangDetail } from '@/apis/datHangApi'
import { fetchHoaDonDetail } from '@/apis/hoaDonApi'
import { fetchPhieuTraHangDetail } from '@/apis/traHangApi'
import { yeuCauXuatHoaDonApi } from '@/apis/yeuCauXuatHoaDonApi'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { CHUYEN_HANG_PERMISSION_CODES } from '@/constants/chucNangs/chuyenHangChucNangs'
import { DAT_HANG_PERMISSION_CODES } from '@/constants/chucNangs/datHangChucNangs'
import { DAT_HANG_NCC_PERMISSION_CODES } from '@/constants/chucNangs/datHangNccChucNangs'
import { HOA_DON_PERMISSION_CODES } from '@/constants/chucNangs/hoaDonChucNangs'
import { KHACH_HANG_PERMISSION_CODES } from '@/constants/chucNangs/khachHangChucNangs'
import { KIEM_KE_PERMISSION_CODES } from '@/constants/chucNangs/kiemKeChucNangs'
import { PHIEU_NHAN_HANG_PERMISSION_CODES } from '@/constants/chucNangs/phieuNhanHangChucNangs'
import { PHIEU_NHAP_KHO_PERMISSION_CODES } from '@/constants/chucNangs/phieuNhapKhoChucNangs'
import { PHIEU_XUAT_KHO_PERMISSION_CODES } from '@/constants/chucNangs/phieuXuatKhoChucNangs'
import { PRODUCT_MUTATE_PERMISSIONS } from '@/constants/chucNangs/productChucNangs'
import { TRA_HANG_PERMISSION_CODES } from '@/constants/chucNangs/traHangChucNangs'
import { YEU_CAU_SUA_CHUA_PERMISSION_CODES } from '@/constants/chucNangs/yeuCauSuaChuaChucNangs'
import { YEU_CAU_XUAT_HOA_DON_PERMISSION_CODES } from '@/constants/chucNangs/yeuCauXuatHoaDonChucNangs'
import { PRODUCT_MODULE_CONFIG } from '@/components/admin/product/convertDefined/productConfig'
import type { IKhachHang } from '@/interfaces/khachHangInterfaces'
import type { IKiemKe } from '@/interfaces/kiemKeInterfaces'
import type { IYeuCauXuatHoaDon } from '@/interfaces/yeuCauXuatHoaDonInterfaces'
import type { BanHangVoucherPrintScreenId } from '@/components/admin/_shared/BanHangVoucherPrintModal'
import type { EntityCodeType } from './entityCodeTypes'

const GiaoDichVoucherListInlineDetail = lazy(
  () => import('@/components/admin/_shared/giaoDich/GiaoDichVoucherListInlineDetail'),
)
const BanHangVoucherPrintModal = lazy(() => import('@/components/admin/_shared/BanHangVoucherPrintModal'))
const HoaDonUpdate = lazy(() => import('@/components/admin/hoaDon/HoaDonUpdate'))
const TraHangUpdate = lazy(() => import('@/components/admin/traHang/TraHangUpdate'))
const DatHangUpdate = lazy(() => import('@/components/admin/datHang/DatHangUpdate'))
const PhieuXuatKhoListInlineDetail = lazy(
  () => import('@/components/admin/phieuXuatKho/partials/PhieuXuatKhoListInlineDetail'),
)
const PhieuXuatKhoUpdate = lazy(() => import('@/components/admin/phieuXuatKho/PhieuXuatKhoUpdate'))
const PhieuNhapKhoListInlineDetail = lazy(
  () => import('@/components/admin/phieuNhapKho/partials/PhieuNhapKhoListInlineDetail'),
)
const PhieuNhapKhoUpdate = lazy(() => import('@/components/admin/phieuNhapKho/PhieuNhapKhoUpdate'))
const ChuyenHangInlineDetail = lazy(() => import('@/components/admin/chuyenHang/partials/ChuyenHangInlineDetail'))
const ChuyenHangUpdate = lazy(() => import('@/components/admin/chuyenHang/ChuyenHangUpdate'))
const KiemKeInlineDetail = lazy(() => import('@/components/admin/kiemKe/partials/KiemKeInlineDetail'))
const KiemKeUpdate = lazy(() => import('@/components/admin/kiemKe/KiemKeUpdate'))
const YeuCauSuaChuaInlineDetail = lazy(
  () => import('@/components/admin/yeuCauSuaChua/partials/YeuCauSuaChuaInlineDetail'),
)
const YeuCauSuaChuaUpdate = lazy(() => import('@/components/admin/yeuCauSuaChua/YeuCauSuaChuaUpdate'))
const YeuCauXuatHoaDonInlineDetail = lazy(
  () => import('@/components/admin/yeuCauXuatHoaDon/partials/YeuCauXuatHoaDonInlineDetail'),
)
const YeuCauXuatHoaDonEditModal = lazy(
  () => import('@/components/admin/yeuCauXuatHoaDon/partials/YeuCauXuatHoaDonEditModal'),
)
const DatHangNccListInlineDetail = lazy(
  () => import('@/components/admin/datHangNcc/partials/DatHangNccListInlineDetail'),
)
const DatHangNccUpdate = lazy(() => import('@/components/admin/datHangNcc/DatHangNccUpdate'))
const PhieuNhanHangListInlineDetail = lazy(
  () => import('@/components/admin/phieuNhanHang/partials/PhieuNhanHangListInlineDetail'),
)
const PhieuNhanHangUpdate = lazy(() => import('@/components/admin/phieuNhanHang/PhieuNhanHangUpdate'))
const KhachHangListInlineDetail = lazy(
  () => import('@/components/admin/khachHang/partials/KhachHangListInlineDetail'),
)
const KhachHangUpdate = lazy(() => import('@/components/admin/khachHang/KhachHangUpdate'))
const ProductListInlineDetail = lazy(() => import('@/components/admin/product/partials/ProductListInlineDetail'))
const ProductPopup = lazy(() => import('@/components/admin/product/partials/ProductPopup'))

export type EntityCodeDetailCtx = {
  id: string
  code?: string
  /** Có khi user được quyền sửa — đóng chi tiết, mở modal sửa. */
  onEdit?: () => void
  onChanged: () => void
}

export type EntityCodeEditCtx = {
  id: string
  onClose: () => void
  /** Lưu xong: đóng modal sửa + báo thay đổi. */
  onSaved: () => void
  /** Chỉ báo thay đổi (modal tự quản lý việc đóng). */
  onChanged: () => void
}

export type EntityCodeConfig = {
  editPermissions: readonly string[]
  renderDetail: (ctx: EntityCodeDetailCtx) => React.ReactNode
  /** Không có → modal chi tiết không hiện nút «Chỉnh sửa». */
  renderEdit?: (ctx: EntityCodeEditCtx) => React.ReactNode
}

const noop = () => {}

type GiaoDichNs = 'hoaDon' | 'traHang' | 'datHang'

const GIAO_DICH_DETAIL: Record<
  GiaoDichNs,
  {
    fetchDetail: (id: string | number) => Promise<{ data: Record<string, unknown> }>
    codeKey: string
    printScreenId: BanHangVoucherPrintScreenId
  }
> = {
  hoaDon: { fetchDetail: fetchHoaDonDetail, codeKey: 'maHoaDon', printScreenId: 'hoa-don' },
  traHang: { fetchDetail: fetchPhieuTraHangDetail, codeKey: 'maPhieuTraHang', printScreenId: 'tra-hang' },
  datHang: { fetchDetail: fetchDatHangDetail, codeKey: 'maDatHang', printScreenId: 'dat-hang' },
}

const GiaoDichCodeDetail: React.FC<EntityCodeDetailCtx & { resourceNs: GiaoDichNs }> = ({
  resourceNs,
  id,
  code,
  onEdit,
  onChanged,
}) => {
  const [printTarget, setPrintTarget] = useState<{ id: string; code: string } | null>(null)
  const cfg = GIAO_DICH_DETAIL[resourceNs]
  const row = useMemo(() => ({ id, [cfg.codeKey]: code }), [cfg.codeKey, code, id])
  return (
    <>
      <GiaoDichVoucherListInlineDetail
        resourceNs={resourceNs}
        row={row}
        fetchDetail={cfg.fetchDetail}
        canEdit={Boolean(onEdit)}
        canDelete={false}
        canDetail={false}
        onEdit={onEdit ?? noop}
        onDelete={noop}
        onDataChanged={onChanged}
        onPrint={(printId, printCode) => setPrintTarget({ id: printId, code: printCode })}
      />
      {printTarget ? (
        <BanHangVoucherPrintModal
          open
          voucherId={printTarget.id}
          voucherCode={printTarget.code}
          screenId={cfg.printScreenId}
          onClose={() => setPrintTarget(null)}
        />
      ) : null}
    </>
  )
}

const YeuCauSuaChuaCodeDetail: React.FC<EntityCodeDetailCtx> = ({ id, code, onEdit, onChanged }) => {
  const { hasAnyChucNangByCode } = useMenuChucNang()
  const can = (c: string) => hasAnyChucNangByCode([c])
  const row = useMemo(() => ({ id, maYeuCau: code }), [code, id])
  return (
    <YeuCauSuaChuaInlineDetail
      row={row}
      canProcess={can(YEU_CAU_SUA_CHUA_PERMISSION_CODES.WEB_BH_YEU_CAU_SUA_CHUA_PROCESS)}
      canComplete={can(YEU_CAU_SUA_CHUA_PERMISSION_CODES.WEB_BH_YEU_CAU_SUA_CHUA_COMPLETE)}
      canCancel={can(YEU_CAU_SUA_CHUA_PERMISSION_CODES.WEB_BH_YEU_CAU_SUA_CHUA_CANCEL)}
      canExport={can(YEU_CAU_SUA_CHUA_PERMISSION_CODES.WEB_BH_YEU_CAU_SUA_CHUA_EXPORT)}
      onDataChanged={async () => onChanged()}
      onEdit={onEdit}
    />
  )
}

/** Panel chi tiết dạng `BanHangEntityListExpandDetailCtx` (row + reload + onEdit). */
const entityListDetail = (
  Component: React.ComponentType<{
    row: Record<string, unknown>
    reload: () => Promise<void>
    onEdit?: () => void
  }>,
  codeKey: string,
): EntityCodeConfig['renderDetail'] => {
  function EntityListCodeDetail({ id, code, onEdit, onChanged }: EntityCodeDetailCtx) {
    const row = useMemo(() => ({ id, [codeKey]: code }), [code, id])
    return <Component row={row} reload={async () => onChanged()} onEdit={onEdit} />
  }
  return (ctx) => <EntityListCodeDetail {...ctx} />
}

const KiemKeCodeDetail: React.FC<EntityCodeDetailCtx> = ({ id, code, onEdit, onChanged }) => {
  const row = useMemo(() => ({ id, maPhieuKiem: code }) as unknown as IKiemKe, [code, id])
  return <KiemKeInlineDetail row={row} reload={async () => onChanged()} onEdit={onEdit} />
}

const KhachHangCodeDetail: React.FC<EntityCodeDetailCtx> = ({ id }) => {
  const row = useMemo(() => ({ id }) as IKhachHang, [id])
  return <KhachHangListInlineDetail row={row} canManage={false} canDelete={false} onEdit={noop} onDelete={noop} />
}

const ProductCodeDetail: React.FC<EntityCodeDetailCtx> = ({ id }) => {
  const row = useMemo(() => ({ id }), [id])
  return <ProductListInlineDetail row={row} canManage={false} layout="drawer" hideViewDetail />
}

const YeuCauXuatHoaDonCodeEdit: React.FC<EntityCodeEditCtx> = ({ id, onClose, onSaved }) => {
  const queryClient = useQueryClient()
  const { data } = useQuery<IYeuCauXuatHoaDon>(['yeuCauXuatHoaDon', id], () => yeuCauXuatHoaDonApi.chiTiet(id), {
    enabled: Boolean(id),
    retry: false,
  })
  if (!data) return null
  return (
    <YeuCauXuatHoaDonEditModal
      open
      yeuCau={data}
      onClose={onClose}
      onSaved={() => {
        void queryClient.invalidateQueries(['yeuCauXuatHoaDon', id])
        onSaved()
      }}
    />
  )
}

export const ENTITY_CODE_REGISTRY: Record<EntityCodeType, EntityCodeConfig> = {
  hoaDon: {
    editPermissions: [HOA_DON_PERMISSION_CODES.WEB_BH_HOA_DON_EDIT],
    renderDetail: (ctx) => <GiaoDichCodeDetail resourceNs="hoaDon" {...ctx} />,
    renderEdit: ({ id, onClose, onSaved }) => <HoaDonUpdate open id={id} onClose={onClose} onSuccess={onSaved} />,
  },
  traHang: {
    editPermissions: [TRA_HANG_PERMISSION_CODES.WEB_BH_TRA_HANG_EDIT],
    renderDetail: (ctx) => <GiaoDichCodeDetail resourceNs="traHang" {...ctx} />,
    renderEdit: ({ id, onClose, onSaved }) => <TraHangUpdate open id={id} onClose={onClose} onSuccess={onSaved} />,
  },
  datHang: {
    editPermissions: [DAT_HANG_PERMISSION_CODES.WEB_BH_DAT_HANG_EDIT],
    renderDetail: (ctx) => <GiaoDichCodeDetail resourceNs="datHang" {...ctx} />,
    renderEdit: ({ id, onClose, onSaved }) => <DatHangUpdate open id={id} onClose={onClose} onSuccess={onSaved} />,
  },
  phieuXuatKho: {
    editPermissions: [PHIEU_XUAT_KHO_PERMISSION_CODES.WEB_BH_PHIEU_XUAT_KHO_EDIT],
    renderDetail: entityListDetail(PhieuXuatKhoListInlineDetail, 'maPhieu'),
    renderEdit: ({ id, onClose, onSaved }) => (
      <PhieuXuatKhoUpdate open id={id} onClose={onClose} onSuccess={onSaved} />
    ),
  },
  phieuNhapKho: {
    editPermissions: [PHIEU_NHAP_KHO_PERMISSION_CODES.WEB_BH_PHIEU_NHAP_KHO_EDIT],
    renderDetail: entityListDetail(PhieuNhapKhoListInlineDetail, 'maPhieu'),
    renderEdit: ({ id, onClose, onSaved }) => (
      <PhieuNhapKhoUpdate open id={id} onClose={onClose} onSuccess={onSaved} />
    ),
  },
  chuyenHang: {
    editPermissions: [CHUYEN_HANG_PERMISSION_CODES.WEB_BH_CHUYEN_HANG_EDIT],
    renderDetail: entityListDetail(ChuyenHangInlineDetail, 'maPhieu'),
    renderEdit: ({ id, onClose, onSaved }) => (
      <ChuyenHangUpdate open id={id} onClose={onClose} onSuccess={onSaved} />
    ),
  },
  kiemKe: {
    editPermissions: [KIEM_KE_PERMISSION_CODES.WEB_BH_KIEM_KE_EDIT],
    renderDetail: (ctx) => <KiemKeCodeDetail {...ctx} />,
    renderEdit: ({ id, onClose, onSaved }) => <KiemKeUpdate open id={id} onClose={onClose} onSuccess={onSaved} />,
  },
  yeuCauSuaChua: {
    editPermissions: [YEU_CAU_SUA_CHUA_PERMISSION_CODES.WEB_BH_YEU_CAU_SUA_CHUA_EDIT],
    renderDetail: (ctx) => <YeuCauSuaChuaCodeDetail {...ctx} />,
    renderEdit: ({ id, onClose, onSaved }) => (
      <YeuCauSuaChuaUpdate open id={id} onClose={onClose} onSuccess={onSaved} />
    ),
  },
  yeuCauXuatHoaDon: {
    editPermissions: [YEU_CAU_XUAT_HOA_DON_PERMISSION_CODES.WEB_BH_YCX_EDIT],
    renderDetail: ({ id, onChanged }) => <YeuCauXuatHoaDonInlineDetail id={id} onChanged={onChanged} />,
    renderEdit: (ctx) => <YeuCauXuatHoaDonCodeEdit {...ctx} />,
  },
  datHangNcc: {
    editPermissions: [DAT_HANG_NCC_PERMISSION_CODES.WEB_BH_DAT_HANG_NCC_EDIT],
    renderDetail: entityListDetail(DatHangNccListInlineDetail, 'maPhieu'),
    renderEdit: ({ id, onClose, onSaved }) => (
      <DatHangNccUpdate open id={id} onClose={onClose} onSuccess={onSaved} />
    ),
  },
  phieuNhanHang: {
    editPermissions: [PHIEU_NHAN_HANG_PERMISSION_CODES.WEB_BH_PHIEU_NHAN_HANG_EDIT],
    renderDetail: entityListDetail(PhieuNhanHangListInlineDetail, 'maPhieuNhan'),
    renderEdit: ({ id, onClose, onSaved }) => (
      <PhieuNhanHangUpdate open id={id} onClose={onClose} onSuccess={onSaved} />
    ),
  },
  khachHang: {
    editPermissions: [KHACH_HANG_PERMISSION_CODES.WEB_BH_KHACH_HANG_EDIT],
    renderDetail: (ctx) => <KhachHangCodeDetail {...ctx} />,
    renderEdit: ({ id, onClose, onSaved }) => <KhachHangUpdate open id={id} onClose={onClose} onSuccess={onSaved} />,
  },
  product: {
    editPermissions: PRODUCT_MUTATE_PERMISSIONS,
    renderDetail: (ctx) => <ProductCodeDetail {...ctx} />,
    renderEdit: ({ id, onClose, onChanged }) => (
      <ProductPopup open onClose={onClose} config={PRODUCT_MODULE_CONFIG} record={{ id }} onSaved={onChanged} />
    ),
  },
}
